import { type RefObject, useEffect, useRef } from 'react';
import { useDataTable } from '../DataTableProvider';
import type { DataTableCellIndexCoordinate } from '../DataTable.model';
import { _notNil } from '@/littledash';

interface UseGridPointerInteractionsOptions {
  onHoverCoordinate?: (coord: DataTableCellIndexCoordinate | null) => void;
}

/**
 * Attaches delegated pointer event listeners to the grid container element to handle
 * cell selection, drag-extension, and activation. Resolves cell coordinates from
 * `data-cell-coordinate` attributes and drives the DataTableService.
 *
 * @param containerRef - Reference to the grid container element
 * @param options.onHoverCoordinate - Optional callback invoked when hovered cell changes.
 *   Reports coordinate or null (on mouseleave). Debounced via requestAnimationFrame.
 */
export const useGridPointerInteractions = (
  containerRef: RefObject<HTMLElement | null>,
  options: UseGridPointerInteractionsOptions = {}
): void => {
  const { dataTableService } = useDataTable();
  const draggingRef = useRef<boolean>(false);
  const lastCoordRef = useRef<DataTableCellIndexCoordinate | undefined>(undefined);
  const lastHoveredCoordRef = useRef<DataTableCellIndexCoordinate | null>(null);
  const { onHoverCoordinate } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (_notNil(container)) {
      const abortController = new AbortController();
      const { signal } = abortController;

      const resolveCoordinate = (target: EventTarget | null): DataTableCellIndexCoordinate | undefined => {
        if (_notNil(target) && target instanceof Element) {
          const cellElement = target.closest('[data-cell-coordinate]');
          if (_notNil(cellElement)) {
            const coordString = cellElement.getAttribute('data-cell-coordinate');
            if (_notNil(coordString)) {
              const [colStr, rowStr] = coordString.split(',');
              const column = Number(colStr);
              const row = Number(rowStr);
              if (!isNaN(column) && !isNaN(row)) {
                return { column, row };
              }
            }
          }
        }
        return undefined;
      };

      const handleMouseDown = (event: MouseEvent): void => {
        if (event.button === 0) {
          const coord = resolveCoordinate(event.target);
          if (_notNil(coord)) {
            dataTableService.selectCell(coord, { append: event.shiftKey });
            draggingRef.current = true;
            lastCoordRef.current = coord;
          }
        }
      };

      let hoverFrame = 0;
      let lastHoverEvent: MouseEvent | null = null;

      const processHover = (): void => {
        hoverFrame = 0;
        if (lastHoverEvent === null) {
          return;
        }
        const coord = resolveCoordinate(lastHoverEvent.target) ?? null;
        const prev = lastHoveredCoordRef.current;
        const changed =
          coord === null || prev === null ? coord !== prev : coord.column !== prev.column || coord.row !== prev.row;

        if (changed && _notNil(onHoverCoordinate)) {
          lastHoveredCoordRef.current = coord;
          onHoverCoordinate(coord);
        }
      };

      const handleMouseOver = (event: MouseEvent): void => {
        if (draggingRef.current && event.buttons === 1) {
          const coord = resolveCoordinate(event.target);
          if (
            _notNil(coord) &&
            (lastCoordRef.current?.column !== coord.column || lastCoordRef.current?.row !== coord.row)
          ) {
            dataTableService.selectCell(coord, { append: true });
            lastCoordRef.current = coord;
          }
        }

        if (_notNil(onHoverCoordinate)) {
          lastHoverEvent = event;
          if (hoverFrame === 0) {
            hoverFrame = requestAnimationFrame(processHover);
          }
        }
      };

      const handleMouseUp = (event: MouseEvent): void => {
        if (draggingRef.current) {
          const coord = resolveCoordinate(event.target);
          if (_notNil(coord)) {
            dataTableService.selectCell(coord, { append: true });
          }
          draggingRef.current = false;
          lastCoordRef.current = undefined;
        }
      };

      const handleDoubleClick = (event: MouseEvent): void => {
        const coord = resolveCoordinate(event.target);
        if (_notNil(coord)) {
          dataTableService.selectCell(coord);
          if (!dataTableService.readonly) {
            dataTableService.activateSelection();
          }
        }
      };

      const handleMouseLeave = (): void => {
        if (_notNil(onHoverCoordinate)) {
          lastHoveredCoordRef.current = null;
          onHoverCoordinate(null);
        }
      };

      const handleDocumentMouseUp = (): void => {
        draggingRef.current = false;
        lastCoordRef.current = undefined;
      };

      container.addEventListener('mousedown', handleMouseDown, { signal });
      container.addEventListener('mouseover', handleMouseOver, { signal });
      container.addEventListener('mouseup', handleMouseUp, { signal });
      container.addEventListener('dblclick', handleDoubleClick, { signal });
      if (_notNil(onHoverCoordinate)) {
        container.addEventListener('mouseleave', handleMouseLeave, { signal });
      }
      document.addEventListener('mouseup', handleDocumentMouseUp, { signal });

      return () => {
        if (hoverFrame !== 0) {
          cancelAnimationFrame(hoverFrame);
        }
        abortController.abort();
      };
    }
  }, [containerRef, dataTableService, onHoverCoordinate]);
};
